<script setup>
import {defineProps, defineEmits, computed, watch, ref, onMounted, onBeforeUnmount} from 'vue'
import Multiselect from '@vueform/multiselect'
import PlatformIcon from '@shared-components/ui/PlatformIcon.vue'

//Props & emits
const emit = defineEmits(['change', 'update:modelValue', 'search-change', 'deselect', 'focusout', 'preselect'])

const props = defineProps({
  modelValue: {},
  id: {
    type: String,
    default: 'the-select',
  },
  options: {
    type: Array,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Select a Value',
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  allow_clear: {
    type: Boolean,
    default: true,
  },
  can_deselect: {
    type: Boolean,
    default: true,
  },
  mode: {
    type: String,
    default: 'single',
  },
  openDirection: {
    type: String,
    default: 'bottom',
  },
  preselect: {
    type: Boolean,
    default: true,
  },
  closeOnDeselect: {
    type: Boolean,
    default: false,
  },
  hideScroll: {
    type: Boolean,
    default: false,
  },
  alwaysShowScroll: {
    type: Boolean,
    default: false,
  },
  isGroup: {
    type: Boolean,
    default: false,
  },
  multipleLabel: {
    type: Function,
    default: undefined,
  },
  // false keeps the selected option(s) in the dropdown (marked as selected) instead of hiding them.
  hideSelected: {
    type: Boolean,
    default: true,
  },
  // `tags` mode only: show just the badges that fit on one row, collapsing the rest into a "+N" chip.
  fitTags: {
    type: Boolean,
    default: false,
  },
  noResultsText: {
    type: String,
    default: 'No results found',
  },
})

// Refs
const theMultiselect = ref()
const rootEl = ref()

// Computed
const isValid = computed(() => !props.required || !!props.modelValue)

// Methods
const onDeselect = value => {
  if (props.closeOnDeselect) {
    theMultiselect.value.deactivate()
  }

  emit('update:modelValue', '')
  emit('deselect', value)
}

const onChange = value => {
  emit('update:modelValue', value)
  emit('change', value)
}

const onSearchChange = value => {
  emit('search-change', value)
}

const onFocusOut = () => {
  emit('focusout')
}

// fitTags keeps the search input collapsed while closed, so a click lands on the wrapper and the
// keystrokes never reach the input - move focus there once the dropdown opens.
const onOpen = () => {
  if (!props.fitTags) {
    return
  }

  rootEl.value?.querySelector('.multiselect-tags-search')?.focus()
}

watch(
  () => props.options,
  () => {
    if (props.options.length === 1 && props.allow_clear === false && props.preselect) {
      emit('update:modelValue', props.options[0].value)
      emit('preselect', props.options[0].value)
    }
  },
  {immediate: true},
)

// fitTags: keep only the badges that share the first row, collapse the rest into a "+N" chip.
// Measured imperatively (remove the limit → read layout → re-apply, all in one frame) so there is no
// flash, and re-run on resize and whenever the selected tags change.
let fitObservers = null

const computeFit = () => {
  const el = rootEl.value

  if (!el) {
    return
  }

  for (const cls of [...el.classList]) {
    if (cls.startsWith('the-select--fit-limit-')) {
      el.classList.remove(cls)
    }
  }

  el.classList.remove('the-select--fit-overflow')
  el.style.removeProperty('--the-select-fit-overflow')

  const tagsEl = el.querySelector('.multiselect-tags')
  const items = tagsEl ? [...tagsEl.querySelectorAll('.multiselect-tag')] : []

  if (items.length < 2) {
    return
  }

  const firstTop = items[0].offsetTop
  let visible = 0

  for (const item of items) {
    if (item.offsetTop !== firstTop) {
      break
    }

    visible += 1
  }

  const overflow = items.length - visible

  if (overflow > 0 && visible > 0) {
    el.style.setProperty('--the-select-fit-overflow', `"+${overflow}"`)
    el.classList.add(`the-select--fit-limit-${visible}`, 'the-select--fit-overflow')
  }
}

onMounted(() => {
  if (!props.fitTags || !rootEl.value) {
    return
  }

  const schedule = () => requestAnimationFrame(computeFit)

  fitObservers = {ro: new ResizeObserver(schedule), mo: new MutationObserver(schedule)}
  fitObservers.ro.observe(rootEl.value)
  fitObservers.mo.observe(rootEl.value, {childList: true, subtree: true})
  schedule()
})

onBeforeUnmount(() => {
  fitObservers?.ro.disconnect()
  fitObservers?.mo.disconnect()
})
</script>

<template>
  <div
    ref="rootEl"
    :class="[
      'the-select-wrapper',
      isValid ? 'valid' : 'invalid',
      hideScroll ? 'hide-scroll' : '',
      mode === 'tags' ? 'tags' : '',
      fitTags ? 'the-select--fit' : '',
      alwaysShowScroll && options.length > 4 ? 'visible-scroll-bar' : '',
    ]"
  >
    <input v-if="required && !isValid" class="d-none" required />

    <Multiselect
      ref="theMultiselect"
      :value="modelValue"
      :options="options"
      :searchable="searchable"
      :disabled="disabled"
      :placeholder="placeholder"
      :mode="mode"
      @input="onChange"
      @deselect="onDeselect"
      @search-change="onSearchChange"
      @focusout="onFocusOut"
      @open="onOpen"
      :id="id"
      :canDeselect="can_deselect"
      :multipleLabel="multipleLabel"
      :hideSelected="hideSelected"
      :openDirection="openDirection"
      :groups="isGroup"
      :noResultsText="noResultsText"
      group-label="title"
      group-options="options"
    >
      <template v-if="!allow_clear" #clear><span /></template>

      <template #singlelabel="{value}">
        <slot v-if="$slots['single-label']" name="single-label" :value="value" />
        <div v-else class="multiselect-single-label">
          <platform-icon v-if="value && value.icon" :platform="value.icon" class="max-w-20 me-1" />
          <span>{{ value.label }}</span>
        </div>
      </template>

      <template #option="{option}">
        <slot v-if="$slots['option']" name="option" :option="option" />
        <template v-else>
          <platform-icon
            v-if="option && option.icon"
            :platform="option.icon"
            class="character-option-icon max-w-20 me-1"
          />
          <span>{{ option.label }}</span>
        </template>
      </template>
    </Multiselect>

    <div v-if="$slots['select-icon']" class="multiselect-position-icon">
      <slot name="select-icon" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.the-select-wrapper {
  position: relative;
}

.multiselect-position-icon {
  height: 100%;
  display: flex;
  align-items: center;
  position: absolute;
  right: 40px;
  top: 0;
}

.visible-scroll-bar .multiselect-dropdown {
  overflow-y: scroll !important;
  scrollbar-gutter: stable;
}

.visible-scroll-bar ::-webkit-scrollbar {
  width: 6px;
}

.visible-scroll-bar ::-webkit-scrollbar-track {
  background: transparent;
}

.visible-scroll-bar ::-webkit-scrollbar-thumb {
  max-height: 40px;
  background: #d1d5db;
  border-radius: 99px;
}

// fitTags: reserve room for the "+N" chip, tighten badges, and hide everything past the measured
// visible count. The chip is absolutely positioned so it can never cause a wrap itself.
.the-select--fit :deep(.multiselect-tags) {
  position: relative;
  padding-right: 38px;
}

.the-select--fit :deep(.multiselect-tag) {
  margin: 3px 0 0 3px;
  padding: 1px 6px;
  font-size: 12px;
}

.the-select--fit :deep(.multiselect-tags-search-wrapper) {
  position: absolute;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.the-select--fit :deep(.multiselect.is-open .multiselect-tags-search-wrapper) {
  position: relative;
  flex: 1 1 60px;
  min-width: 60px;
  height: 100%;
  overflow: visible;
}

.the-select--fit-overflow :deep(.multiselect-tags)::after {
  content: var(--the-select-fit-overflow);
  position: absolute;
  top: 4px;
  right: 8px;
  padding: 0 6px;
  border-radius: 4px;
  background: #6b7280;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
}

@for $i from 1 through 11 {
  .the-select--fit-limit-#{$i} :deep(.multiselect-tag:nth-of-type(n + #{$i + 1})) {
    display: none;
  }
}
</style>
